import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Trash2, Download, Send, Eraser, ArrowRight, HeartPulse, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, coverSrc, fmtXof, apiError } from "../lib/api";
import { useQuote } from "../context/QuoteContext";
import { useCurrency } from "../context/CurrencyContext";
import { Reveal } from "../components/Motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const BREAKDOWN = [
  ["Analyse et conception", 0.20],
  ["Développement / Production", 0.50],
  ["Tests et validation", 0.15],
  ["Livraison et formation", 0.15],
];

const CIVILITIES = ["M.", "Mme", "Dr.", "Pr."];
const COUNTRIES = [
  ["Côte d'Ivoire", "🇨🇮"], ["Sénégal", "🇸🇳"], ["Burkina Faso", "🇧🇫"], ["Mali", "🇲🇱"], ["Niger", "🇳🇪"],
  ["Togo", "🇹🇬"], ["Bénin", "🇧🇯"], ["Guinée", "🇬🇳"], ["Cameroun", "🇨🇲"], ["Gabon", "🇬🇦"],
  ["RD Congo", "🇨🇩"], ["Madagascar", "🇲🇬"], ["Maroc", "🇲🇦"], ["Nigeria", "🇳🇬"], ["Ghana", "🇬🇭"],
  ["France", "🇫🇷"], ["Belgique", "🇧🇪"], ["Suisse", "🇨🇭"], ["Canada", "🇨🇦"], ["États-Unis", "🇺🇸"],
  ["Royaume-Uni", "🇬🇧"], ["Allemagne", "🇩🇪"], ["Chine", "🇨🇳"], ["Autre", "🌍"],
];
const PAYMENTS = ["Mobile Money (Orange, MTN, Wave)", "Virement bancaire", "Carte bancaire internationale", "Chèque (Côte d'Ivoire uniquement)", "À définir"];

const EMPTY_CLIENT = {
  civility: "M.", name: "", function: "", institution: "", country: "Côte d'Ivoire",
  city: "", email: "", phone: "", payment_method: "À définir",
  observations: "", rdv_visio: false, dispos: "",
};

// Délai express : 70 % du délai standard (arrondi), supplément 30 %
const expressDelay = (delay) => {
  const m = /(\d+)/.exec(delay || "");
  if (!m) return "sur estimation";
  const n = Math.max(1, Math.round(parseInt(m[1], 10) * 0.7));
  return delay.replace(m[1], String(n));
};

export default function Devis() {
  const { items, update, remove, clear } = useQuote();
  const { rates } = useCurrency();
  const [client, setClient] = useState(EMPTY_CLIENT);
  const [busy, setBusy] = useState("");
  const [confirmClear, setConfirmClear] = useState(false);
  const [sentNumber, setSentNumber] = useState("");

  const year = new Date().getFullYear();
  const provNumber = `DEV-${year}-PROV`;

  const eurRate = rates.EUR || 655.957;
  const usdRate = rates.USD || 610;
  const fmtCur = (cur, xof) => {
    const rate = cur === "EUR" ? eurRate : usdRate;
    try {
      return new Intl.NumberFormat("fr-FR", { style: "currency", currency: cur, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(xof / rate);
    } catch { return `${Math.round(xof / rate)} ${cur}`; }
  };

  const lineHt = (it) => {
    const base = it.price * (it.qty || 1);
    return it.maintenance ? base + Math.round(base * 0.10) : base;
  };
  const totals = useMemo(() => {
    const ht = items.reduce((s, it) => s + lineHt(it), 0);
    return { ht, tvaAf: Math.round(ht * 0.18), ttcAf: Math.round(ht * 1.18), tvaInt: Math.round(ht * 0.20), ttcInt: Math.round(ht * 1.20) };
  }, [items]);

  const payload = () => ({
    items: items.map((it) => ({
      product_id: it.product_id, title: it.title, category: it.category || "",
      subtype: it.subtype || "", price: it.price, qty: it.qty || 1,
      delay: it.delay || "", conditions: it.conditions || "", maintenance: !!it.maintenance,
    })),
    civility: client.civility, name: client.name, function: client.function,
    institution: client.institution, country: client.country, city: client.city,
    email: client.email, phone: client.phone, payment_method: client.payment_method,
    observations: client.observations, rdv_visio: client.rdv_visio, dispos: client.dispos,
  });

  const downloadPdf = async () => {
    if (!client.name || !client.email) { toast.error("Renseignez au minimum votre nom et votre email (section 4)"); return; }
    setBusy("pdf");
    try {
      const res = await api.post("/devis/pdf", payload(), { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${provNumber}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Devis PDF téléchargé (référence provisoire)");
    } catch (e) { toast.error(apiError(e, "Génération impossible")); }
    finally { setBusy(""); }
  };

  const submit = async () => {
    if (!client.name || client.name.length < 2) { toast.error("Nom et Prénoms obligatoires"); return; }
    if (!client.email) { toast.error("Email professionnel obligatoire"); return; }
    setBusy("send");
    try {
      const { data } = await api.post("/devis", payload());
      setSentNumber(data.number);
      clear();
    } catch (e) { toast.error(apiError(e, "Envoi impossible")); }
    finally { setBusy(""); }
  };

  if (sentNumber) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-24 text-center" data-testid="devis-success">
        <CheckCircle2 size={56} className="mx-auto text-emerald-500" />
        <h1 className="mt-6 font-display text-3xl font-bold text-navy">Demande envoyée !</h1>
        <p className="mt-3 text-slate-600">
          Votre demande de devis <span className="font-mono font-semibold text-navy" data-testid="devis-number">{sentNumber}</span> a
          bien été transmise au Cabinet CARIS Santé. Vous recevrez une proposition détaillée sous 24 à 48 heures ouvrées
          à l'adresse <strong>{client.email}</strong>.
        </p>
        <Link to="/catalogue" className="mt-8 inline-flex items-center gap-2 rounded-full bg-ciel px-7 py-3.5 text-sm font-semibold text-white hover:bg-sky-700">
          Continuer mes achats <ArrowRight size={15} />
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-6 py-14" data-testid="devis-page">
      {/* EN-TÊTE */}
      <div className="flex items-start gap-4">
        <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-navy text-white"><HeartPulse size={26} /></span>
        <div>
          <p className="font-mono text-xs uppercase tracking-widest text-ciel">Cabinet CARIS Santé</p>
          <h1 className="mt-1 font-display text-3xl font-bold tracking-tight text-navy md:text-4xl">Demande de Devis</h1>
          <p className="mt-2 text-sm text-slate-500">Recevez une proposition détaillée sous 24 à 48 heures ouvrées</p>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="mt-14 rounded-2xl border border-dashed border-slate-300 bg-white p-16 text-center" data-testid="devis-empty">
          <FileText size={40} className="mx-auto text-slate-300" />
          <p className="mt-4 font-display text-lg font-semibold text-navy">Votre devis est vide</p>
          <p className="mt-2 text-sm text-slate-500">Ajoutez des produits ou services depuis leur fiche, via le bouton « Demander un devis ».</p>
          <Link to="/catalogue" className="mt-6 inline-flex items-center gap-2 rounded-full bg-ciel px-6 py-3 text-sm font-semibold text-white hover:bg-sky-700" data-testid="devis-goto-catalogue">
            Explorer le catalogue <ArrowRight size={15} />
          </Link>
        </div>
      ) : (
        <>
          {/* SECTION 1 — PRODUITS SÉLECTIONNÉS */}
          <Reveal className="mt-12">
            <h2 className="font-display text-lg font-bold text-navy">1. Produit(s) sélectionné(s)</h2>
            <p className="mt-1 text-xs text-slate-400">Référence provisoire : <span className="font-mono">{provNumber}</span> (référence officielle attribuée à l'envoi)</p>
            <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200 bg-white" data-testid="devis-items-table">
              <table className="w-full min-w-[950px] text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left font-mono text-[10px] uppercase tracking-wider text-slate-400">
                    <th className="p-3">Réf.</th><th className="p-3">Produit</th><th className="p-3">Délai estimé</th>
                    <th className="p-3 text-right">Coût HT</th><th className="p-3 text-right">TVA 18 % (UEMOA)</th>
                    <th className="p-3 text-right">TTC Afrique</th><th className="p-3 text-right">TTC Intl (TVA 20 %)</th><th className="p-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, i) => {
                    const ht = lineHt(it);
                    return (
                      <tr key={it.product_id} className="border-b border-slate-100" data-testid={`devis-item-${it.product_id}`}>
                        <td className="p-3 font-mono text-xs text-slate-500">{provNumber}-{String(i + 1).padStart(2, "0")}</td>
                        <td className="p-3">
                          <div className="flex items-center gap-3">
                            <img src={coverSrc(it.cover)} alt="" className="h-10 w-12 rounded object-cover" />
                            <div>
                              <p className="font-medium text-navy">{it.title}</p>
                              <p className="text-xs text-slate-400">{it.category}{it.subtype ? ` · ${it.subtype}` : ""}</p>
                              {it.short_description && <p className="mt-0.5 max-w-[260px] truncate text-xs text-slate-400">{it.short_description}</p>}
                            </div>
                          </div>
                        </td>
                        <td className="p-3 text-xs text-slate-600">{it.delay || "À définir"}</td>
                        <td className="p-3 text-right font-mono text-xs font-semibold text-navy">{fmtXof(ht)}</td>
                        <td className="p-3 text-right font-mono text-xs text-slate-500">{fmtXof(Math.round(ht * 0.18))}</td>
                        <td className="p-3 text-right font-mono text-xs font-semibold text-navy">{fmtXof(Math.round(ht * 1.18))}</td>
                        <td className="p-3 text-right font-mono text-xs text-slate-500">
                          {fmtCur("EUR", Math.round(ht * 1.20))}<br />{fmtCur("USD", Math.round(ht * 1.20))}
                        </td>
                        <td className="p-3">
                          <button onClick={() => remove(it.product_id)} data-testid={`devis-remove-${it.product_id}`} title="Retirer du devis"
                            className="rounded-lg border border-red-200 p-2 text-red-500 transition-colors duration-200 hover:bg-red-50">
                            <Trash2 size={13} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Reveal>

          {/* SECTION 2 — RÉCAPITULATIF FINANCIER */}
          <Reveal className="mt-10">
            <h2 className="font-display text-lg font-bold text-navy">2. Récapitulatif financier</h2>
            <div className="mt-4 grid gap-4 md:grid-cols-2" data-testid="devis-summary">
              <div className="rounded-xl border border-slate-200 bg-white p-6">
                <p className="font-mono text-xs uppercase tracking-widest text-ciel">Tarification Afrique (UEMOA)</p>
                <dl className="mt-4 space-y-2 text-sm">
                  <div className="flex justify-between"><dt className="text-slate-500">Sous-total HT</dt><dd className="font-mono font-semibold text-navy" data-testid="devis-ht">{fmtXof(totals.ht)}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-500">TVA 18 %</dt><dd className="font-mono text-slate-600">{fmtXof(totals.tvaAf)}</dd></div>
                  <div className="flex justify-between border-t border-slate-100 pt-2"><dt className="font-semibold text-navy">Total TTC</dt><dd className="font-mono font-bold text-navy" data-testid="devis-ttc-afrique">{fmtXof(totals.ttcAf)}</dd></div>
                  <div className="flex justify-between text-xs"><dt className="text-slate-400">Équivalent USD</dt><dd className="font-mono text-slate-500">{fmtCur("USD", totals.ttcAf)}</dd></div>
                  <div className="flex justify-between text-xs"><dt className="text-slate-400">Équivalent EUR</dt><dd className="font-mono text-slate-500">{fmtCur("EUR", totals.ttcAf)}</dd></div>
                </dl>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-6">
                <p className="font-mono text-xs uppercase tracking-widest text-ciel">Tarification internationale</p>
                <dl className="mt-4 space-y-2 text-sm">
                  <div className="flex justify-between"><dt className="text-slate-500">Sous-total HT</dt><dd className="font-mono text-slate-600">{fmtCur("EUR", totals.ht)} / {fmtCur("USD", totals.ht)}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-500">TVA 20 %</dt><dd className="font-mono text-slate-600">{fmtCur("EUR", totals.tvaInt)} / {fmtCur("USD", totals.tvaInt)}</dd></div>
                  <div className="flex justify-between border-t border-slate-100 pt-2"><dt className="font-semibold text-navy">Total TTC</dt><dd className="font-mono font-bold text-navy" data-testid="devis-ttc-intl">{fmtCur("EUR", totals.ttcInt)} / {fmtCur("USD", totals.ttcInt)}</dd></div>
                  <div className="flex justify-between text-xs"><dt className="text-slate-400">Équivalent FCFA</dt><dd className="font-mono text-slate-500">{fmtXof(totals.ttcInt)}</dd></div>
                </dl>
              </div>
            </div>
            <p className="mt-3 text-xs italic text-slate-400">
              Les tarifs internationaux sont donnés à titre indicatif selon le taux de change du jour.
              Le montant final est fixé d'un commun accord avant signature.
            </p>
          </Reveal>

          {/* SECTION 3 — DÉTAIL PAR PRODUIT */}
          <Reveal className="mt-10">
            <h2 className="font-display text-lg font-bold text-navy">3. Détail par produit</h2>
            <div className="mt-4 space-y-5">
              {items.map((it, i) => {
                const base = it.price * (it.qty || 1);
                return (
                  <div key={it.product_id} className="rounded-xl border border-slate-200 bg-white p-6" data-testid={`devis-detail-${it.product_id}`}>
                    <p className="font-mono text-xs text-slate-400">Référence : {provNumber}-{String(i + 1).padStart(2, "0")}</p>
                    <h3 className="mt-1 font-display text-base font-semibold text-navy">{it.title}</h3>
                    <p className="text-xs text-slate-500">{it.category}{it.subtype ? ` / ${it.subtype}` : ""}</p>
                    {it.description && <p className="mt-3 text-sm leading-relaxed text-slate-600">{it.description.slice(0, 400)}{it.description.length > 400 ? "…" : ""}</p>}
                    <div className="mt-4 grid gap-4 sm:grid-cols-2">
                      <div>
                        <label className="text-xs font-semibold text-slate-600">Délai de réalisation standard</label>
                        <Input data-testid={`devis-delay-${it.product_id}`} className="mt-1.5" placeholder="Ex : 8 semaines" value={it.delay}
                          onChange={(e) => update(it.product_id, { delay: e.target.value })} />
                        <p className="mt-1.5 text-xs text-slate-400">
                          Délai express (supplément 30 %) : <span className="font-semibold text-slate-600">{it.delay ? expressDelay(it.delay) : "—"}</span>
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-slate-600">Décomposition du coût</p>
                        <dl className="mt-2 space-y-1 text-xs">
                          {BREAKDOWN.map(([label, pct]) => (
                            <div key={label} className="flex justify-between"><dt className="text-slate-500">{label}</dt><dd className="font-mono text-slate-600">{fmtXof(Math.round(base * pct))}</dd></div>
                          ))}
                          <div className="flex items-center justify-between">
                            <dt className="flex items-center gap-2 text-slate-500">
                              <Checkbox checked={it.maintenance} onCheckedChange={(v) => update(it.product_id, { maintenance: !!v })}
                                data-testid={`devis-maintenance-${it.product_id}`} />
                              Maintenance 1ère année (optionnel, 10 %)
                            </dt>
                            <dd className="font-mono text-slate-600">{it.maintenance ? fmtXof(Math.round(base * 0.10)) : "—"}</dd>
                          </div>
                          <div className="flex justify-between border-t border-slate-100 pt-1.5"><dt className="font-semibold text-navy">Total HT</dt><dd className="font-mono font-bold text-navy">{fmtXof(lineHt(it))}</dd></div>
                        </dl>
                      </div>
                    </div>
                    <div className="mt-4">
                      <label className="text-xs font-semibold text-slate-600">Conditions particulières</label>
                      <textarea data-testid={`devis-conditions-${it.product_id}`} rows={2} value={it.conditions}
                        placeholder="Observations, conditions spécifiques, inclusions/exclusions…"
                        onChange={(e) => update(it.product_id, { conditions: e.target.value })}
                        className="mt-1.5 w-full rounded-md border border-slate-300 p-3 text-sm outline-none focus:ring-2 focus:ring-ciel" />
                    </div>
                  </div>
                );
              })}
            </div>
          </Reveal>

          {/* SECTION 4 — DEMANDEUR */}
          <Reveal className="mt-10">
            <h2 className="font-display text-lg font-bold text-navy">4. Informations du demandeur</h2>
            <div className="mt-4 grid gap-4 rounded-xl border border-slate-200 bg-white p-6 sm:grid-cols-2" data-testid="devis-client-form">
              <Select value={client.civility} onValueChange={(v) => setClient({ ...client, civility: v })}>
                <SelectTrigger data-testid="devis-civility"><SelectValue /></SelectTrigger>
                <SelectContent>{CIVILITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
              <Input data-testid="devis-name" required placeholder="Nom et Prénoms *" value={client.name}
                onChange={(e) => setClient({ ...client, name: e.target.value })} />
              <Input data-testid="devis-function" placeholder="Fonction / Titre" value={client.function}
                onChange={(e) => setClient({ ...client, function: e.target.value })} />
              <Input data-testid="devis-institution" placeholder="Institution / Entreprise / Établissement" value={client.institution}
                onChange={(e) => setClient({ ...client, institution: e.target.value })} />
              <Select value={client.country} onValueChange={(v) => setClient({ ...client, country: v })}>
                <SelectTrigger data-testid="devis-country"><SelectValue /></SelectTrigger>
                <SelectContent>{COUNTRIES.map(([c, f]) => <SelectItem key={c} value={c}>{f} {c}</SelectItem>)}</SelectContent>
              </Select>
              <Input data-testid="devis-city" placeholder="Ville" value={client.city}
                onChange={(e) => setClient({ ...client, city: e.target.value })} />
              <Input data-testid="devis-email" type="email" required placeholder="Email professionnel *" value={client.email}
                onChange={(e) => setClient({ ...client, email: e.target.value })} />
              <Input data-testid="devis-phone" placeholder="Téléphone / WhatsApp" value={client.phone}
                onChange={(e) => setClient({ ...client, phone: e.target.value })} />
              <div className="sm:col-span-2">
                <label className="text-xs font-semibold text-slate-600">Moyen de paiement envisagé</label>
                <Select value={client.payment_method} onValueChange={(v) => setClient({ ...client, payment_method: v })}>
                  <SelectTrigger data-testid="devis-payment" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{PAYMENTS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </Reveal>

          {/* SECTION 5 — OBSERVATIONS */}
          <Reveal className="mt-10">
            <h2 className="font-display text-lg font-bold text-navy">5. Observations et besoins spécifiques</h2>
            <div className="mt-4 rounded-xl border border-slate-200 bg-white p-6">
              <textarea data-testid="devis-observations" rows={4} value={client.observations}
                placeholder="Décrivez ici tout besoin spécifique, contrainte technique, délai impératif ou question particulière…"
                onChange={(e) => setClient({ ...client, observations: e.target.value })}
                className="w-full rounded-md border border-slate-300 p-3 text-sm outline-none focus:ring-2 focus:ring-ciel" />
              <label className="mt-4 flex items-center gap-2 text-sm text-slate-600">
                <Checkbox checked={client.rdv_visio} onCheckedChange={(v) => setClient({ ...client, rdv_visio: !!v })} data-testid="devis-rdv" />
                Je souhaite un rendez-vous de présentation en visioconférence avant validation du devis
              </label>
              {client.rdv_visio && (
                <Input data-testid="devis-dispos" className="mt-3" placeholder="Disponibilités préférées (ex : mardi et jeudi, 14h-17h GMT)" value={client.dispos}
                  onChange={(e) => setClient({ ...client, dispos: e.target.value })} />
              )}
            </div>
          </Reveal>

          {/* BOUTONS */}
          <div className="mt-10 flex flex-wrap gap-3 border-t border-slate-200 pt-6">
            <Button data-testid="devis-download-pdf" variant="outline" onClick={downloadPdf} disabled={!!busy}
              className="rounded-full py-5 text-navy">
              {busy === "pdf" ? <Loader2 className="mr-1.5 animate-spin" size={15} /> : <Download size={15} className="mr-1.5" />}
              Télécharger le devis en PDF
            </Button>
            <Button data-testid="devis-submit" onClick={submit} disabled={!!busy}
              className="flex-1 rounded-full bg-ciel py-5 text-sm font-semibold text-white hover:bg-sky-700">
              {busy === "send" ? <Loader2 className="mr-1.5 animate-spin" size={15} /> : <Send size={15} className="mr-1.5" />}
              Envoyer la demande de devis
            </Button>
            <Button data-testid="devis-clear" variant="outline" onClick={() => setConfirmClear(true)} disabled={!!busy}
              className="rounded-full py-5 text-red-500 hover:text-red-600">
              <Eraser size={15} className="mr-1.5" /> Vider le devis
            </Button>
            <Link to="/catalogue" data-testid="devis-continue"
              className="flex items-center gap-1.5 rounded-full px-5 py-2.5 text-sm font-semibold text-slate-500 transition-colors duration-200 hover:text-ciel">
              Continuer mes achats <ArrowRight size={14} />
            </Link>
          </div>
        </>
      )}

      <Dialog open={confirmClear} onOpenChange={setConfirmClear}>
        <DialogContent data-testid="devis-clear-dialog">
          <DialogHeader><DialogTitle className="font-display text-navy">Vider le devis ?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Tous les produits seront retirés de votre demande de devis. Votre panier n'est pas affecté.</p>
          <div className="mt-3 flex gap-3">
            <Button variant="outline" onClick={() => setConfirmClear(false)} className="flex-1 rounded-full">Annuler</Button>
            <Button data-testid="devis-clear-confirm" onClick={() => { clear(); setConfirmClear(false); toast.success("Devis vidé"); }}
              className="flex-1 rounded-full bg-red-500 text-white hover:bg-red-600">Confirmer</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
